import { canContributeToReview, isReviewAdministrator } from "./reviewPermissions";
import ReviewHeaderActions from "./ReviewHeaderActions";
import { useDiscussionState } from "../../components/Inspections/discussionState";
import ReviewDiscussion from "./ReviewDiscussion";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getInspectionList,
  downloadInspectionDocument,
  getLoggedInUser,
  getUsersList,
} from "../../helpers/backend_helper";
import InspectionChecklistReview from "./InspectionChecklistReview";
import InspectionChecklistSelection from "./InspectionChecklistSelection";
import InspectionReviewWorkspace from "./InspectionReviewWorkspace";
import FullScreenPageLayout from "../../components/Common/FullScreenPageLayout";
import ReviewWizardSteps from "./ReviewWizardSteps";
import ReviewReports from "./ReviewReports";

const accountId = (account) => Number(account?.id ?? account?.userId);

const roleName = (account) =>
  account?.role?.name ||
  account?.roleName ||
  account?.userRole?.name ||
  account?.designation ||
  "";

const activeTaskFor = (review, userId) =>
  (review?.reviewWorkflowTasks || []).find(
    (task) =>
      Number(task.assignedTo) === Number(userId) &&
      /active/i.test(task.workflowStatus || "")
  );

const assignedTo = (review, userId) =>
  Boolean(userId) &&
  (Number(review?.assignedTo) === Number(userId) ||
    Number(review?.assignedUser?.id) === Number(userId) ||
    Boolean(activeTaskFor(review, userId)));

export default function InspectionReviewPage({ checklist = false }) {
  const { inspectionreviewId: inspectionReviewId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);

  const [account, setAccount] = useState(null);
  const discussionStateKey = `review-discussion:${accountId(account) || "loading"}:${inspectionReviewId}`;
  const [discussionOpen, setDiscussionOpen] = useDiscussionState(`${discussionStateKey}:open`, true);
  const [discussionScroll, setDiscussionScroll] = useDiscussionState(`${discussionStateKey}:scroll`, 0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      getInspectionList(inspectionReviewId),
      getLoggedInUser().catch(() => null),
    ])
      .then(([reviewResponse, user]) => {
        if (!reviewResponse?.status)
          throw new Error(
            reviewResponse?.message || "Unable to load this inspection review."
          );
        const selected = (reviewResponse.data || []).find(
          (item) => Number(item.id) === Number(inspectionReviewId)
        );
        if (!selected)
          throw new Error(
            "This inspection review is unavailable or you no longer have access to it."
          );
        if (!active) return;
        setReview(selected);
        setAccount(user || null);

      })
      .catch((reason) => {
        if (active)
          setError(reason?.message || "Unable to load this inspection review.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [inspectionReviewId]);

  useEffect(() => {
    let active = true;
    if (!review || checklist) return;
    getUsersList().then((response) => {
      if (active) setUsers(response?.status ? (response.data || []).filter((item) => item.isBlocked !== 1) : []);
    }).catch(() => { if (active) setUsers([]); });
    return () => { active = false; };
  }, [review?.id, checklist]);

  const userId = accountId(account);
  const searchParams = new URLSearchParams(location.search);
  const requestedTaskId = Number(searchParams.get("workflowTaskId")) || null;
  const assignedReview = searchParams.get("assignedReview") === "1";
  const workflowTask = useMemo(() => {
    if (!review) return null;
    if (assignedReview) {
      const assignedChecklistTasks = (review.reviewWorkflowTasks || []).filter(
        (task) =>
          task.taskType === "ChecklistReview" &&
          Number(task.assignedTo) === Number(userId)
      );
      if (assignedChecklistTasks.length) {
        return {
          ...assignedChecklistTasks[0],
          id: null,
          taskType: "ChecklistReviewGroup",
          checklistIds: [
            ...new Set(
              assignedChecklistTasks
                .map((task) => Number(task.checklistId))
                .filter(Boolean)
            ),
          ],
          workflowTaskIds: assignedChecklistTasks
            .filter((task) => /active/i.test(task.workflowStatus || ""))
            .map((task) => Number(task.id))
            .filter(Boolean),
        };
      }
    }
    return (
      (review.reviewWorkflowTasks || []).find(
        (task) => Number(task.id) === requestedTaskId
      ) ||
      activeTaskFor(review, userId) ||
      null
    );
  }, [assignedReview, requestedTaskId, review, userId]);
  const isEngagementMember =
    Number(account?.roleId) === 4 ||
    /engagement\s+member/i.test(roleName(account));
  const canParticipate = canContributeToReview(account) && assignedTo(review, userId);
  const readOnly = searchParams.get("readOnly") === "1" || !canParticipate;
  const readOnlyBanner = checklist && readOnly ? <p role="status" className="alert alert-info m-3">Read-only · Assigned to {review?.assignedUser?.fullName || "the assigned reviewer"}. Scope, answers, evidence, queries, findings and submissions cannot be changed.</p> : null;
  const summaryPath = `/inspectionreview/${inspectionReviewId}`;
  const engagementOrigin = location.state?.engagementReturnPath;
  const fromEngagement = /^\/engagements\/(list|\d+\/preview)$/.test(engagementOrigin || "");
  const reviewListPath = fromEngagement ? engagementOrigin : Number(review?.inspectionCycleId) > 0
    ? `/inspections/${review.inspectionCycleId}/preview`
    : "/review-tasks";
  const reviewListLabel = fromEngagement ? engagementOrigin === "/engagements/list" ? "Engagements" : "Engagement" : Number(review?.inspectionCycleId) > 0 ? "Inspection Cycle" : "My Work";
  const sourcePath = location.state?.fromPath || summaryPath;
  const sourceLabel = location.state?.fromLabel || "Review Summary";
  const breadcrumbSourceLabel =
    sourceLabel === "Review Summary"
      ? `${review?.engagementDetail?.name || "Engagement"} - Review Summary`
      : sourceLabel;
  const checklistBreadcrumbs = [
    {
      label: reviewListLabel,
      path: reviewListPath,
      onClick: () => navigate(reviewListPath),
    },
    ...(sourcePath !== reviewListPath
      ? [
          {
            label: breadcrumbSourceLabel,
            path: sourcePath,
            onClick: () => navigate(sourcePath, { state: location.state }),
          },
        ]
      : []),
  ];

  if (loading)
    return (
      <main className="page-content piers-page">
        <div role="status">Loading inspection review…</div>
      </main>
    );
  if (error || !review)
    return (
      <main className="page-content piers-page">
        <div role="alert">{error}</div>
        <Link to={reviewListPath}>Back to {reviewListLabel}</Link>
      </main>
    );

  const selectedChecklistId = Number(searchParams.get("checklistId")) || null;
  const allChecklistsPath = `${summaryPath}/checklist${location.search ? `?${new URLSearchParams([...searchParams].filter(([key]) => key !== "checklistId" && key !== "checklistName" && key !== "view" && key !== "questionId" && key !== "queryId"))}` : ""}`;
  const reportsView = searchParams.get("view") === "reports";
  const wizardSteps = <ReviewWizardSteps currentStep={reportsView ? 3 : checklist ? 2 : 1}
    engagementName={review.engagementDetail?.name}
    checklistName={selectedChecklistId ? searchParams.get("checklistName") || "Checklist" : undefined}
    onSummary={() => navigate(summaryPath, { state: location.state })}
    onReports={() => navigate(`${location.pathname}?${new URLSearchParams({ ...Object.fromEntries(searchParams), view: "reports" })}`, { state: location.state })}
    onReviewChecklist={() => { const params = new URLSearchParams(searchParams); params.delete("view"); navigate(`${summaryPath}/checklist?${params}`, { state: location.state }); }} />;
  const discussionAction = <button type="button" className="review-wide-layout__discussion-button" aria-expanded={discussionOpen} onClick={() => setDiscussionOpen((open) => !open)}><i className="bx bx-conversation" aria-hidden="true" /> Review Discussion</button>;
  const headerActions = <ReviewHeaderActions review={review} discussionAction={discussionAction} onClose={() => navigate(reviewListPath)} />;
  const withDiscussion = (content) => <div className={`review-wide-layout${discussionOpen ? "" : " is-discussion-closed"}`}>
    <div className="review-wide-layout__workspace">{checklist && !selectedChecklistId ? readOnlyBanner : null}{content}</div>
    <aside hidden={!discussionOpen} className="review-wide-layout__discussion checklist-review-page" aria-labelledby="review-discussion-heading">
      <header className="review-wide-layout__discussion-heading">
        <h2 id="review-discussion-heading"><i className="bx bx-conversation" aria-hidden="true" /> Review Discussion</h2>
        <button type="button" aria-label="Close review discussion" title="Close discussion" onClick={() => setDiscussionOpen(false)}><i className="bx bx-x" /></button>
      </header>
      <div className="review-wide-layout__discussion-body" ref={(element) => { if (element && discussionOpen && element.scrollTop !== discussionScroll) element.scrollTop = discussionScroll; }} onScroll={(event) => { if (discussionOpen) setDiscussionScroll(event.currentTarget.scrollTop); }}>
        <ReviewDiscussion readOnly={!account || (checklist && readOnly)} stateKey={discussionStateKey} key={review.id} inspectionId={review.id} onDownload={(file) => downloadInspectionDocument(review.id, file.id, file.fileName || file.name)} />
      </div>
    </aside>
  </div>;
  if (reportsView) return withDiscussion(<FullScreenPageLayout className="review-summary-page" steps={wizardSteps} showAskPiers={false}
    breadcrumbs={[{ label: reviewListLabel, to: reviewListPath }, { label: "Reports" }]}
    showUserInfo actions={headerActions}>
    <ReviewReports review={review} />
  </FullScreenPageLayout>);
  if (checklist && !canParticipate && !isReviewAdministrator(account))
    return <FullScreenPageLayout actions={headerActions} showAskPiers={false}><p role="alert" className="alert alert-info m-3">This review is assigned to {review.assignedUser?.fullName || "the assigned reviewer"}. Only the assigned reviewer can review the checklist or raise queries.</p></FullScreenPageLayout>;
  if (checklist && !selectedChecklistId)
    return withDiscussion(<InspectionChecklistSelection
      wizardSteps={wizardSteps} discussionAction={discussionAction} headerActions={headerActions}
      review={review}
      readOnly={readOnly}
      summaryState={location.state}
      breadcrumbs={checklistBreadcrumbs}
      onClose={() => navigate(reviewListPath)}
      onSelect={(item) => {
        const params = new URLSearchParams(location.search);
        params.set("checklistId", item.id);
        params.set("checklistName", item.name);
        navigate(`${summaryPath}/checklist?${params}`, { state: location.state });
      }}
    />);

  if (checklist)
    return withDiscussion(
      <InspectionChecklistReview
        readOnly={readOnly}
        wizardSteps={wizardSteps} discussionAction={discussionAction} headerActions={headerActions}
        dataId={review.id}
        inspectionId={review.id}
        dataDetail={review}
        workflowTask={
          workflowTask?.workflowStatus === "Active" && Number(workflowTask?.checklistId) === selectedChecklistId
          ? workflowTask
          : (review.reviewWorkflowTasks || []).find((task) =>
            Number(task.checklistId) === selectedChecklistId &&
            Number(task.assignedTo) === Number(userId) &&
            /active/i.test(task.workflowStatus || "")
          ) || (review.reviewWorkflowTasks || []).find((task) =>
            Number(task.checklistId) === selectedChecklistId &&
            Number(task.assignedTo) === Number(userId) &&
            ["ChecklistReview", "ChecklistSignOff"].includes(task.taskType)
          ) || { checklistId: selectedChecklistId, taskType: "ChecklistReview", workflowStatus: "Pending" }
        }
        initialQuestionId={Number(searchParams.get("questionId")) || null}
        initialQueryId={Number(searchParams.get("queryId")) || null}
        backToMyWorkAction={sourceLabel === "My Work" ? () => navigate("/review-tasks") : undefined}
        selectedChecklistId={selectedChecklistId}
        selectedChecklistName={searchParams.get("checklistName") || "Checklist"}
        allChecklistsAction={(options) => navigate(options?.defineScope ? `${summaryPath}?scopeChecklistId=${selectedChecklistId}` : options?.openSummary ? summaryPath : allChecklistsPath, { state: location.state })}
        reportsAction={() => navigate(`${summaryPath}/checklist?view=reports`, { state: location.state })}
        breadcrumbItems={checklistBreadcrumbs}
        closeAction={() => navigate(reviewListPath)}
      />
    );

  return withDiscussion(
    <FullScreenPageLayout
      className="review-summary-page"
      steps={wizardSteps}
      breadcrumbs={[
        {
          label: reviewListLabel,
          to: reviewListPath,
        },
        { label: `${review.engagementDetail?.name || "Engagement"} - Review Summary` },
      ]}
      showAskPiers={false}
      showUserInfo actions={headerActions}
    >
    <InspectionReviewWorkspace
      backPath={reviewListPath}
      backLabel={`Back to ${reviewListLabel}`}
      dataDetail={review}
      canStart={canParticipate}
      canOpenReadOnly={isReviewAdministrator(account)}
      canManage={!isEngagementMember}
      users={users}
      initialScopeChecklistId={Number(searchParams.get("scopeChecklistId")) || null}
      focusedWorkflowTaskId={requestedTaskId}
      closeAction={() => navigate(reviewListPath)}
      openChecklistAction={(options) =>
        navigate(
          `${summaryPath}/checklist?${new URLSearchParams({
            ...(workflowTask?.id ? { workflowTaskId: workflowTask.id } : {}),
            ...(options?.readOnly ? { readOnly: "1" } : {}),
          })}`,
          {
            state: {
              ...location.state,
              fromPath: `${location.pathname}${location.search}`,
              fromLabel: "Review Summary",
            },
          }
        )
      }
    />
    </FullScreenPageLayout>
  );
}
